import { withoutProductImages } from "@/cms/lib/productCards";
import { Registry } from "./index";
import type { BlockDefinition } from "./types";
import { hasExactlyOneRef } from "@/cms/lib/mediaShared";
import {
  TABLE_MAX_COLS,
  TABLE_MAX_ROWS,
  TABLE_SCHEMA_VERSION,
  sanitizeTableDoc,
} from "@/cms/blocks/table";

const defs: BlockDefinition[] = [
  {
    type: "heading",
    currentSchemaVersion: 1,
    validate(data) {
      const d = data as { level: 1 | 2 | 3; text: string };
      if (!d || typeof d.text !== "string" || !d.text.trim()) {
        throw new Error("heading.text required");
      }
      if (![1, 2, 3].includes(d.level)) {
        throw new Error("heading.level invalid");
      }
      return d;
    },
  },
  {
    type: "contact",
    currentSchemaVersion: 1,
    validate(data) {
      if (!data || typeof data !== "object" || Array.isArray(data)) {
        return {};
      }
      return {};
    },
  },
  {
    type: "table",
    currentSchemaVersion: 1,
    validate(data) {
      const d = data as {
        schemaVersion?: number;
        doc?: any;
        caption?: string | null;
        variant?: "default" | "compact";
        fixedRow?: number | null;
        fixedCol?: number | null;
      };
      if (!d || typeof d !== "object") {
        throw new Error("table data required");
      }
      const schemaVersion =
        typeof d.schemaVersion === "number"
          ? d.schemaVersion
          : TABLE_SCHEMA_VERSION;
      if (schemaVersion !== TABLE_SCHEMA_VERSION) {
        throw new Error("table.schemaVersion must be 1");
      }
      if (!d.doc) {
        throw new Error("table.doc required");
      }
      const { doc, stats } = sanitizeTableDoc(d.doc, {
        maxRows: TABLE_MAX_ROWS,
        maxCols: TABLE_MAX_COLS,
      });
      if (stats.tableCount !== 1) {
        throw new Error("table.doc must contain exactly one table");
      }
      if (stats.rows > TABLE_MAX_ROWS || stats.cols > TABLE_MAX_COLS) {
        throw new Error(
          `table exceeds max size (${TABLE_MAX_ROWS} rows x ${TABLE_MAX_COLS} cols)`,
        );
      }
      const caption =
        typeof d.caption === "string" && d.caption.trim()
          ? d.caption.trim()
          : undefined;
      const variant: "default" | "compact" =
        d.variant === "compact" ? "compact" : "default";
      const fixedRow =
        typeof d.fixedRow === "number" &&
        Number.isInteger(d.fixedRow) &&
        d.fixedRow >= 0 &&
        d.fixedRow < stats.rows
          ? d.fixedRow
          : undefined;
      const fixedCol =
        typeof d.fixedCol === "number" &&
        Number.isInteger(d.fixedCol) &&
        d.fixedCol >= 0 &&
        d.fixedCol < stats.cols
          ? d.fixedCol
          : undefined;
      return {
        schemaVersion,
        doc,
        variant,
        ...(caption ? { caption } : {}),
        ...(typeof fixedRow === "number" ? { fixedRow } : {}),
        ...(typeof fixedCol === "number" ? { fixedCol } : {}),
      };
    },
  },
  {
    type: "paragraph",
    currentSchemaVersion: 1,
    validate(data) {
      const d = data as {
        richText?: any;
        text?: string;
        variant?: "default" | "shortDescription";
      };
      if (!d) throw new Error("paragraph data required");
      if (!d.richText && !d.text) {
        throw new Error("paragraph.richText or text required");
      }
      if (d.variant && !["default", "shortDescription"].includes(d.variant)) {
        throw new Error("paragraph.variant invalid");
      }
      return d;
    },
  },
  {
    type: "image",
    currentSchemaVersion: 1,
    validate(data) {
      const d = data as {
        assetId?: string;
        url?: string;
        alt?: string;
        width?: number;
        height?: number;
        variant?: "default" | "hero";
      };
      if (!d) throw new Error("image data required");
      if (!hasExactlyOneRef(d)) {
        throw new Error("image must have exactly one of assetId or url");
      }
      if (d.variant && !["default", "hero"].includes(d.variant)) {
        throw new Error("image.variant invalid");
      }
      return d;
    },
  },
  {
    type: "divider",
    currentSchemaVersion: 1,
    validate(data) {
      const d = data as { variant?: "line" | "space" };
      if (d?.variant && !["line", "space"].includes(d.variant)) {
        throw new Error("divider.variant invalid");
      }
      return d ?? {};
    },
  },
  {
    type: "list",
    currentSchemaVersion: 1,
    validate(data) {
      const d = data as { ordered?: boolean; items: string[] };
      if (!Array.isArray(d?.items)) {
        throw new Error("list.items must be array");
      }
      return d;
    },
  },
  {
    type: "faq",
    currentSchemaVersion: 1,
    validate(data) {
      const d = data as {
        items?: Array<{
          id?: string;
          question?: { richText?: any; text?: string };
          answer?: { richText?: any; text?: string };
          image?: {
            assetId?: string;
            url?: string;
            alt?: string;
            width?: number;
            height?: number;
            previewUrl?: string;
            sourceName?: string;
          };
          imagePosition?: "inlineLeft" | "inlineRight" | "top" | "bottom";
        }>;
      };
      if (!Array.isArray(d?.items)) {
        throw new Error("faq.items must be array");
      }
      d.items.forEach((item, index) => {
        if (!item || typeof item !== "object") {
          throw new Error(`faq.items[${index}] invalid`);
        }
        const hasQuestion =
          item.question?.richText ||
          (typeof item.question?.text === "string" &&
            item.question.text.trim());
        if (!hasQuestion) {
          throw new Error(`faq.items[${index}].question required`);
        }
        const hasAnswer =
          item.answer?.richText ||
          (typeof item.answer?.text === "string" && item.answer.text.trim());
        if (!hasAnswer) {
          throw new Error(`faq.items[${index}].answer required`);
        }
        if (item.image) {
          if (!hasExactlyOneRef(item.image)) {
            throw new Error(
              `faq.items[${index}].image must have exactly one of assetId or url`,
            );
          }
        }
        if (item.imagePosition) {
          if (
            !["inlineLeft", "inlineRight", "top", "bottom"].includes(
              item.imagePosition,
            )
          ) {
            throw new Error(
              `faq.items[${index}].imagePosition must be one of inlineLeft, inlineRight, top, bottom`,
            );
          }
        }
      });
      return d;
    },
  },
  {
    type: "quote",
    currentSchemaVersion: 1,
    validate(data) {
      const d = data as {
        text?: string;
        cite?: string;
        variant?: "default" | "bracket";
      };
      if (!d || !d.text?.trim()) {
        throw new Error("quote.text required");
      }
      const variant: "default" | "bracket" =
        d.variant === "bracket" ? "bracket" : "default";
      return { text: d.text.trim(), ...(d.cite ? { cite: d.cite } : {}), variant };
    },
  },
  {
    type: "link",
    currentSchemaVersion: 1,
    validate(data) {
      const d = data as {
        title?: string;
        mode?: "url" | "content";
        variant?: "default" | "cta";
        url?: string;
        contentSlug?: string;
        contentId?: string | null;
      };
      if (!d?.title?.trim()) {
        throw new Error("link.title required");
      }
      const variant: "default" | "cta" =
        d.variant === "cta" ? "cta" : "default";
      const mode = d.mode === "content" ? "content" : "url";
      const url = typeof d.url === "string" ? d.url.trim() : "";
      const contentSlug =
        typeof d.contentSlug === "string" ? d.contentSlug.trim() : "";
      if (variant === "cta") {
        if (!url) throw new Error("link.url required for cta variant");
        return { title: d.title.trim(), variant, mode: "url", url };
      }
      if (mode === "url") {
        if (!url) throw new Error("link.url required for url mode");
        return { title: d.title.trim(), mode, url, variant };
      }
      if (!contentSlug) {
        throw new Error("link.contentSlug required for content mode");
      }
      const contentId =
        typeof d.contentId === "string" && d.contentId.trim()
          ? d.contentId.trim()
          : undefined;
      return {
        title: d.title.trim(),
        mode,
        variant,
        contentSlug,
        ...(contentId ? { contentId } : {}),
      };
    },
  },
  {
    type: "embed",
    currentSchemaVersion: 1,
    validate(data) {
      const d = data as { url: string };
      if (!d?.url?.trim()) {
        throw new Error("embed.url required");
      }
      return d;
    },
  },
  {
    type: "productCard",
    currentSchemaVersion: 1,
    validate(data) {
      const d = data as {
        products?: Array<{
          productId?: string;
          partNumber?: string | null;
        }>;
        productId?: string;
      };
      if (Array.isArray(d?.products)) {
        if (!d.products.length) {
          throw new Error("productCard.products requires at least one product");
        }
        d.products.forEach((product, index) => {
          if (!product?.productId || !product.productId.trim()) {
            throw new Error(
              `productCard.products[${index}].productId required`,
            );
          }
        });
        return withoutProductImages(d as Record<string, unknown>);
      }
      if (!d?.productId?.trim()) {
        throw new Error("productId required");
      }
      return withoutProductImages(d as Record<string, unknown>);
    },
  },
  {
    type: "contentReference",
    currentSchemaVersion: 1,
    validate(data) {
      const d = data as {
        contentId?: string;
        type?: string;
        status?: string;
        title_en?: string | null;
        title_fa?: string | null;
        slug_en?: string | null;
        slug_fa?: string | null;
      };
      if (!d?.contentId?.trim()) {
        throw new Error("contentId required");
      }
      if (!d?.type?.trim()) {
        throw new Error("type required");
      }
      if (!d?.status?.trim()) {
        throw new Error("status required");
      }
      return d;
    },
  },
  {
    type: "reference",
    currentSchemaVersion: 1,
    validate(data) {
      const d = data as {
        referenceId?: string;
        referenceIds?: string[];
      };
      if (!d) throw new Error("reference data required");
      const referenceIds = Array.isArray(d.referenceIds)
        ? d.referenceIds.filter((id) => typeof id === "string" && id.trim())
        : [];
      const hasSingle = typeof d.referenceId === "string" && d.referenceId.trim();
      if (!referenceIds.length && !hasSingle) {
        throw new Error("reference.referenceIds or reference.referenceId required");
      }
      return d;
    },
  },
];

defs.forEach(Registry.register);
